import React from "react";
import { Modal, Typography, Button, Divider, List, Tag } from "antd";
import { ClockCircleOutlined, CloseOutlined, ArrowRightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import { mapUnitToAcronym, formatStatusWithActor } from "../../../../utils/ouberUtils";


const { Text, Title } = Typography;

const getPassengerName = (m) => {
  if (m.employee) {
    return `${m.employee.first_name || ""} ${m.employee.last_name || ""}`.trim() || "Unknown";
  }
  return m.external_passenger_name || "Guest";
};

const getPassengerStatusTagClass = (status) => {
  if (status === "Approved by AO" || status === "Created by AO") return "tag-approved";
  if (status === "Completed Trip") return "tag-completed";
  if (status === "Requested to Join") return "tag-pending";
  return "tag-cancelled";
};

const AdminViewTripRequestModal = ({ isOpen, onClose, requestData, onCreateLinkedTrip, onEdit, onDecline, onUpdatePassengerStatus }) => {
  const { details: profile, account_types = [] } = useSelector((state) => state.accountDetailSlice);
  
  const userUnits = useMemo(() => {
    if (!profile?.positions) return [];
    const todayStr = dayjs().format("YYYY-MM-DD");
    return profile.positions
      .filter(p => {
        const isAO = [
          'Administrative Officer I',
          'Administrative Officer II',
          'Administrative Officer III',
          'Administrative Officer IV',
          'Administrative Officer V',
        ].includes(p.position_name);
        if (!isAO) return false;

        const endDate = p.pivot?.position_end_date || p.position_end_date;
        if (!endDate) return true;
        return endDate >= todayStr;
      })
      .map(p => (p.unit ? mapUnitToAcronym(p.unit) : null))
      .filter(Boolean);
  }, [profile]);
  
  const isSuperAdmin = profile?.account_type === "Super Admin" || account_types.includes("superadmin") || account_types.includes("xadmin");

  if (!requestData) return null;

  const isLinked = !!requestData.trip; // If the request relies on an active pool trip
  const headerBgColor = "#862633";
  
  const canManage = isSuperAdmin || userUnits.includes(requestData.unit) || (requestData.trip && userUnits.includes(requestData.trip.organizing_unit));
  // Approving/declining a passenger who joined a trip is the trip creator's
  // call (or a Super Admin's), not scoped to unit like canManage above.
  const isTripOwner = isSuperAdmin || (requestData.trip?.created_by_employee_id && requestData.trip.created_by_employee_id === profile?.id);

  const renderRequestDetails = () => (
    <div style={{ marginTop: '16px' }}>
      <Title level={5} style={{ margin: 0, marginBottom: '12px' }}>Request Details</Title>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div>
          <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Requestor/Head Name</Text>
          <Text strong>{requestData.employee ? `${requestData.employee.first_name} ${requestData.employee.last_name}` : (requestData.external_passenger_name || "N/A")}</Text>
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Faculty Unit</Text>
          <Text strong>{requestData.unit || "N/A"}</Text>
        </div>
        
        {(!isLinked || (isLinked && requestData.trip?.allowed_multiple_pickup)) && (
          <>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Pickup Time</Text>
              <Text>
                <ClockCircleOutlined style={{ marginRight: '4px' }} /> 
                {requestData.estimated_pickup_time 
                  ? dayjs(requestData.estimated_pickup_time, "HH:mm:ss").format("h:mm A") 
                  : (requestData.trip?.pickup_time ? dayjs(requestData.trip.pickup_time, "HH:mm:ss").format("h:mm A") : "N/A")}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Pickup Venue</Text>
              <Text>{requestData.pickup_venue || "N/A"}</Text>
            </div>
          </>
        )}

        {!isLinked && (
          <>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Trip Date</Text>
              <Text>{requestData.trip_date ? dayjs(requestData.trip_date).format("MMMM D, YYYY") : "N/A"}</Text>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Trip Route</Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text>{requestData.pickup_venue}</Text> 
                <ArrowRightOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} /> 
                <Text>{requestData.dropoff_location}</Text>
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Purpose of Trip</Text>
              <Text>{requestData.trip_purpose || "Official Business"}</Text>
            </div>
          </>
        )}
      </div>

      {(() => {
        const passengers = (requestData.group_members || [])
          .sort((a, b) => a.trip_request_id - b.trip_request_id);

        if (passengers.length === 0) return null;

        return (
          <>
            <Divider style={{ margin: '16px 0 12px' }} />
            <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginBottom: '8px' }}>
              Passengers ({passengers.length})
            </Text>
            <List
              size="small"
              bordered
              dataSource={passengers}
              rowKey={(m) => m.trip_request_id}
              renderItem={(m) => {
                const isLeader = m.trip_request_id === m.request_group_id;
                const canDecide = isLinked && isTripOwner && m.request_status === "Requested to Join" && onUpdatePassengerStatus;
                return (
                  <List.Item
                    actions={canDecide ? [
                      <Button size="small" type="primary" className="ouber-btn-forest" onClick={() => onUpdatePassengerStatus(m.trip_request_id, "Approved by AO")}>Approve</Button>,
                      <Button size="small" danger onClick={() => onUpdatePassengerStatus(m.trip_request_id, "Declined by AO")}>Decline</Button>,
                    ] : []}
                  >
                    <List.Item.Meta
                      title={
                        <span>
                          {getPassengerName(m)}
                          <Tag className={getPassengerStatusTagClass(m.request_status)} style={{ border: 'none', marginLeft: '8px', fontSize: '10px' }}>
                            {formatStatusWithActor(m.request_status, m.status_changed_by)}
                          </Tag>
                          {isLeader && <Tag color="blue" style={{ fontSize: '10px' }}>Head of the Party</Tag>}
                          {!m.employee_id && <Tag color="orange" style={{ fontSize: '10px' }}>Guest</Tag>}
                        </span>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </>
        );
      })()}
    </div>
  );

  const renderLinkedTripDetails = () => {
    const trip = requestData.trip;
    if (!trip) return null;

    const formatTime = (timeStr) => {
      if (!timeStr || timeStr === 'N/A') return 'TBD';
      return dayjs(timeStr, "HH:mm:ss").format("h:mm A");
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Title level={5} style={{ margin: 0 }}>Trip Details</Title>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Route</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text>{trip.pickup_venue}</Text> 
              <ArrowRightOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} /> 
              <Text>{trip.dropoff_location}</Text>
            </div>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Date</Text>
            <Text>{dayjs(trip.trip_date).format("MMMM D, YYYY")}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Driver</Text>
            <Text>{(trip.driver && typeof trip.driver === 'object' && trip.driver.driver_name) ? trip.driver.driver_name : (trip.custom_driver_name || "Unassigned")}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Contact Number</Text>
            <Text>{(trip.driver && typeof trip.driver === 'object' && trip.driver.contact_number) ? trip.driver.contact_number : (trip.custom_driver_contact || "N/A")}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Pickup Time</Text>
            <Text>{formatTime(trip.pickup_time)}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Estimated Arrival</Text>
            <Text>{formatTime(trip.estimated_arrival_time)}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Vehicle</Text>
            <Text>{(trip.vehicle && typeof trip.vehicle === 'object' && trip.vehicle.vehicle_model) ? trip.vehicle.vehicle_model : (trip.custom_vehicle_name || "Unassigned")}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Plate Number</Text>
            <Text>{(trip.vehicle && typeof trip.vehicle === 'object' && trip.vehicle.plate_number) ? trip.vehicle.plate_number : (trip.custom_vehicle_plate || "N/A")}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Capacity</Text>
            <Text>{trip.passengers ? trip.passengers.length : 0}/{trip.trip_passenger_capacity || "N/A"}</Text>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Purpose of Trip</Text>
            <Text>{trip.trip_purpose || "Official Business"}</Text>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      zIndex={1050}
      closeIcon={<CloseOutlined style={{ color: "white" }} />}
      styles={{
        header: {
          backgroundColor: headerBgColor,
          padding: "16px 24px",
          borderRadius: "8px 8px 0 0",
          margin: "-20px -24px 24px -24px"
        },
        title: { color: "white", margin: 0 },
        body: { maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden', margin: '0 -24px', padding: '0 24px' }
      }}
      title={<Title level={5} style={{ color: 'white', margin: 0, fontSize: '16px' }}>View Trip Request</Title>}
      className="white-close-icon"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {isLinked ? (
          // Instance 2 (Linked Trip existing)
          <>
            {renderLinkedTripDetails()}
            <Divider style={{ margin: '12px 0' }} />
            {renderRequestDetails()}
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
              <Button onClick={onClose}>Close</Button>
              {isLinked && requestData.request_status === "Requested to Join" && canManage && (
                <Button type="primary" className="ouber-btn-forest" onClick={() => onEdit(requestData)}>
                  Edit Request
                </Button>
              )}
            </div>
          </>
        ) : (
          // Instance 1 (Standalone custom request)
          <>
             {(requestData.request_status === "Requested to Join") && (
               <div style={{ backgroundColor: '#fffbe6', padding: '12px', borderRadius: '8px', borderLeft: '4px solid #faad14', marginBottom: '8px' }}>
                 <Text type="warning" style={{ fontSize: '13px', color: '#d46b08' }}>
                   <strong>Note:</strong> This request is currently unlinked. You must link it to a new created trip to approve it.
                   {requestData.group_members && requestData.group_members.length > 1 && (
                     <> Creating the trip here will also approve and include all {requestData.group_members.length - 1} other passenger(s) below — no need to do this separately for each of them.</>
                   )}
                 </Text>
               </div>
             )}
             {renderRequestDetails()}
             
             <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
              <Button onClick={onClose}>Close</Button>
              
              {canManage && (requestData.request_status === "Requested to Join") && (
                <>
                  <Button className="ouber-btn-danger" onClick={() => onDecline(requestData)}>Decline Request</Button>
                  <Button type="primary" className="ouber-btn-forest" onClick={() => onCreateLinkedTrip(requestData)}>
                    Create Trip
                  </Button>
                </>
              )}

            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AdminViewTripRequestModal;
